import { useTranslation } from "react-i18next";
import DropDown from "@/components/forms/inputs/dropDown";
import { LmsStatusPill } from "@/components/lms/lmsStatusChip";
import {
  getLmsCourseStatusOptions,
  getLmsCourseStatusPresentation,
  type LmsCourseActivationStatusValue,
} from "@/components/lms/lmsCourseStatusPresentation";

export type LmsCourseStatusOption = {
  value: LmsCourseActivationStatusValue;
  label: string;
};

interface Props {
  status?: string | null;
  disabled?: boolean;
  isUpdating?: boolean;
  onStatusChange: (nextStatus: LmsCourseStatusOption) => void | Promise<void>;
}

const renderStatusOption = (option: LmsCourseStatusOption) => {
  const presentation = getLmsCourseStatusPresentation(option.value);

  return (
    <LmsStatusPill tone={presentation.tone} label={option.label} />
  );
};

export default function LmsCourseStatusDropDown({
  status,
  disabled = false,
  isUpdating = false,
  onStatusChange,
}: Props) {
  const { t } = useTranslation();
  const statusOptions = getLmsCourseStatusOptions(t);
  const selectedOption =
    statusOptions.find((option) => option.value === status) ?? null;
  const presentation = getLmsCourseStatusPresentation(status);

  return (
    <DropDown
      options={statusOptions}
      externalValue={selectedOption}
      onChange={(value: LmsCourseStatusOption | null) => {
        if (!value || value.value === status) {
          return;
        }

        void onStatusChange(value);
      }}
      name="course-overview-status-summary"
      isClearable={false}
      isSearchable={false}
      disabled={disabled || isUpdating}
      showLoadingIndicator={isUpdating}
      formatOptionLabel={renderStatusOption}
      customStyles={{
        control: (base, state) => ({
          ...base,
          minHeight: "2.75rem",
          borderRadius: "999px",
          borderColor: presentation.color,
          boxShadow: state.isFocused
            ? `0 0 0 1px ${presentation.color}`
            : "none",
          backgroundColor: "#ffffff",
          cursor: disabled || isUpdating ? "not-allowed" : "pointer",
          "&:hover": {
            borderColor: presentation.color,
          },
        }),
        valueContainer: (base) => ({
          ...base,
          padding: "0.15rem 0.65rem",
        }),
        singleValue: (base) => ({
          ...base,
          margin: 0,
        }),
        menu: (base) => ({
          ...base,
          borderRadius: "0.875rem",
          overflow: "hidden",
          boxShadow: "0px 12px 32px -8px rgba(32, 30, 30, 0.18)",
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused ? "#f8f4fc" : "#ffffff",
          color: "#201e1e",
          cursor: "pointer",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: presentation.color,
          backgroundColor: presentation.backgroundColor,
          borderRadius: "0.75rem",
          padding: "4px",
          margin: "0 0.3rem",
          "&:hover": {
            color: presentation.color,
          },
        }),
        loadingIndicator: (base) => ({
          ...base,
          color: presentation.color,
        }),
      }}
    />
  );
}
